import { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { format, startOfYear } from "date-fns";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import * as XLSX from "xlsx";
import {
  ArrowDown,
  ArrowUp,
  Calendar,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Coins,
  Download,
  Equal,
  FileSpreadsheet,
  FileText,
  Filter,
  Info,
  MoreVertical,
  Printer,
  ReceiptText,
  RefreshCcw,
  WalletCards,
} from "lucide-react";

import { api } from "@/lib/api";
import { useAuth } from "@/context/auth.context";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

type StatementType =
  | "Contribution"
  | "Savings"
  | "Loan Request"
  | "Loan Disbursement"
  | "Loan Repayment"
  | "Interest Payment"
  | "Other Charge";

type StatementRow = {
  id: string;
  date: Date;
  description: string;
  type: StatementType;
  category: string;
  amount: number;
  shares: number | null;
  paymentMethod: string;
  reference: string;
  balance: number;
};

const PAGE_SIZE = 8;
const ALL = "all";

const TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: ALL, label: "All Transactions" },
  { value: "Contribution", label: "Contributions" },
  { value: "Savings", label: "Savings" },
  { value: "Loan Request", label: "Loan Requests" },
  { value: "Loan Disbursement", label: "Loan Disbursements" },
  { value: "Loan Repayment", label: "Loan Repayments" },
  { value: "Interest Payment", label: "Interest Payments" },
  { value: "Other Charge", label: "Other Charges" },
];

const TYPE_STYLES: Record<StatementType, string> = {
  Contribution: "border-blue-200 bg-blue-50 text-blue-700",
  Savings: "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Loan Request": "border-slate-200 bg-slate-50 text-slate-700",
  "Loan Disbursement": "border-orange-200 bg-orange-50 text-orange-700",
  "Loan Repayment": "border-violet-200 bg-violet-50 text-violet-700",
  "Interest Payment": "border-red-200 bg-red-50 text-red-700",
  "Other Charge": "border-slate-200 bg-slate-50 text-slate-700",
};

const formatMoney = (value: number) => `${Math.round(value || 0).toLocaleString()} FRW`;
const formatAmount = (value: number) =>
  `${value >= 0 ? "+" : "-"}${Math.abs(Math.round(value || 0)).toLocaleString()}`;

const asNumber = (value: any) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const asDate = (value: any): Date | null => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const dateInputToDate = (value: string, endOfDay = false) =>
  new Date(`${value}${endOfDay ? "T23:59:59" : "T00:00:00"}`);

const emptyValue = (value: any, fallback = "-") =>
  value === undefined || value === null || value === "" ? fallback : value;

const isSavingsContribution = (contribution: any) => {
  const text = [
    contribution?.contributionType,
    contribution?.type,
    contribution?.source,
    contribution?.description,
    contribution?.category,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return text.includes("saving") || text.includes("voluntary") || text.includes("deposit");
};

const getContributionShares = (contribution: any, shareValue: number) => {
  const explicitShares = asNumber(
    contribution?.shareCount ?? contribution?.shares ?? contribution?.numberOfShares
  );
  if (explicitShares > 0) return explicitShares;

  const amount = asNumber(contribution?.amount ?? contribution?.depositAmount ?? contribution?.declaredAmount);
  return shareValue > 0 ? amount / shareValue : 0;
};

const getLoanPayments = (loan: any) => {
  if (Array.isArray(loan?.payments)) return loan.payments;
  if (Array.isArray(loan?.repayments)) return loan.repayments;
  if (Array.isArray(loan?.paymentHistory)) return loan.paymentHistory;
  return [];
};

export default function AccountStatement() {
  const { user, portalMode } = useAuth();
  const [dateFrom, setDateFrom] = useState(format(startOfYear(new Date()), "yyyy-MM-dd"));
  const [dateTo, setDateTo] = useState(format(new Date(), "yyyy-MM-dd"));
  const [typeFilter, setTypeFilter] = useState(ALL);
  const [categoryFilter, setCategoryFilter] = useState(ALL);
  const [methodFilter, setMethodFilter] = useState(ALL);
  const [page, setPage] = useState(1);

  const profileEndpoint =
    portalMode === "member" ? `/members/${user?.id}` : `/teachers/${user?.id}`;

  const { data: profileData, isLoading: loadingProfile } = useQuery(
    ["member-statement-profile", portalMode, user?.id],
    async () => {
      const { data } = await api.get(profileEndpoint);
      return data.data || data;
    },
    { enabled: !!user?.id }
  );

  const { data: settings } = useQuery(["member-statement-settings"], async () => {
    const { data } = await api.get("/settings");
    return data.data || data;
  });

  const { data: contributions = [], isLoading: loadingContributions } = useQuery(
    ["member-statement-contributions", user?.id],
    async () => {
      const { data } = await api.get("/contributions/my?limit=2000");
      const result = data.data || data;
      return Array.isArray(result) ? result : result?.contributions || [];
    },
    { enabled: !!user?.id }
  );

  const { data: loans = [], isLoading: loadingLoans } = useQuery(
    ["member-statement-loans", portalMode, user?.id],
    async () => {
      const endpoint = portalMode === "member" ? "/loans/my" : `/teachers/${user?.id}/loans`;
      const { data } = await api.get(endpoint);
      const result = data.data || data;
      return Array.isArray(result) ? result : result?.loans || [];
    },
    { enabled: !!user?.id }
  );

  const profile = profileData || user || {};
  const shareValue = asNumber(settings?.savings?.shareValue);
  const memberName =
    profile?.fullName ||
    [profile?.firstName, profile?.lastName].filter(Boolean).join(" ").trim() ||
    user?.fullName ||
    user?.name ||
    user?.email?.split("@")[0] ||
    "Member";
  const memberInitials =
    `${profile?.firstName?.charAt(0) || ""}${profile?.lastName?.charAt(0) || ""}` ||
    memberName.slice(0, 2).toUpperCase();
  const memberCode = profile?.memberCode || profile?.teacherId || profile?.id || user?.id;
  const joinedDate = asDate(profile?.joinedAt || profile?.joinedSavingsGroupDate);

  const rows = useMemo(() => {
    const items: Omit<StatementRow, "balance">[] = [];

    contributions.forEach((contribution: any) => {
      const amount = asNumber(contribution?.amount ?? contribution?.depositAmount ?? contribution?.declaredAmount);
      if (amount <= 0) return;

      const date = asDate(contribution?.contributionDate || contribution?.paidAt || contribution?.createdAt);
      if (!date) return;

      const isSavings = isSavingsContribution(contribution);
      const type: StatementType = isSavings ? "Savings" : "Contribution";
      const category = isSavings ? "Voluntary Savings" : "Monthly Contribution";

      items.push({
        id: `contribution-${contribution?.id || contribution?._id || items.length}`,
        date,
        description:
          contribution?.description ||
          (isSavings ? "Savings Deposit" : `Monthly Contribution - ${format(date, "MMMM yyyy")}`),
        type,
        category,
        amount,
        shares: getContributionShares(contribution, shareValue),
        paymentMethod: contribution?.paymentMethod || contribution?.method || contribution?.source || "-",
        reference:
          contribution?.referenceNumber ||
          contribution?.reference ||
          contribution?.transactionId ||
          contribution?.id ||
          "-",
      });
    });

    loans.forEach((loan: any) => {
      const loanId = loan?.id || loan?._id || loan?.loanNumber || items.length;
      const requestedAmount = asNumber(loan?.amount ?? loan?.loanAmount ?? loan?.requestedAmount);
      const requestedDate = asDate(loan?.applicationDate || loan?.requestedAt || loan?.createdAt);

      if (requestedDate && requestedAmount > 0) {
        items.push({
          id: `loan-request-${loanId}`,
          date: requestedDate,
          description: loan?.purpose ? `Loan Request - ${loan.purpose}` : "Loan Request",
          type: "Loan Request",
          category: "Loan Request",
          amount: 0,
          shares: null,
          paymentMethod: "-",
          reference: loan?.referenceNumber || loan?.loanNumber || loanId,
        });
      }

      const status = String(loan?.status || "").toLowerCase();
      const disbursementDate = asDate(
        loan?.disbursementDate || loan?.disbursedAt || loan?.approvedAt || loan?.createdAt
      );
      const disbursedAmount = asNumber(
        loan?.disbursedAmount ?? loan?.amountDisbursed ?? loan?.netAmount
      );
      const amountForDisbursement =
        disbursedAmount > 0
          ? disbursedAmount
          : ["active", "disbursed", "approved"].includes(status)
            ? requestedAmount
            : 0;

      if (disbursementDate && amountForDisbursement > 0) {
        items.push({
          id: `loan-disbursement-${loanId}`,
          date: disbursementDate,
          description: loan?.purpose ? `Loan Disbursement - ${loan.purpose}` : "Loan Disbursement",
          type: "Loan Disbursement",
          category: "Loan Disbursement",
          amount: amountForDisbursement,
          shares: null,
          paymentMethod: loan?.disbursementMethod || loan?.paymentMethod || "-",
          reference: loan?.referenceNumber || loan?.loanNumber || loanId,
        });
      }

      const interestAmount =
        requestedAmount > amountForDisbursement
          ? requestedAmount - amountForDisbursement
          : asNumber(loan?.interestDeducted ?? loan?.interestAmount);
      if (disbursementDate && interestAmount > 0) {
        items.push({
          id: `loan-interest-${loanId}`,
          date: disbursementDate,
          description: "Interest Deduction",
          type: "Interest Payment",
          category: "Interest",
          amount: -interestAmount,
          shares: null,
          paymentMethod: "-",
          reference: loan?.interestReference || loan?.referenceNumber || loan?.loanNumber || loanId,
        });
      }

      const feeAmount = asNumber(loan?.applicationFee ?? loan?.processingFee ?? loan?.fees);
      if (requestedDate && feeAmount > 0) {
        items.push({
          id: `loan-fee-${loanId}`,
          date: requestedDate,
          description: "Loan Application Fee",
          type: "Other Charge",
          category: "Loan Fee",
          amount: -feeAmount,
          shares: null,
          paymentMethod: loan?.feePaymentMethod || "-",
          reference: loan?.feeReference || loan?.referenceNumber || loan?.loanNumber || loanId,
        });
      }

      getLoanPayments(loan).forEach((payment: any, index: number) => {
        const paymentAmount = asNumber(payment?.amount ?? payment?.paidAmount);
        if (paymentAmount <= 0) return;

        const paymentDate = asDate(payment?.paymentDate || payment?.paidAt || payment?.createdAt);
        if (!paymentDate) return;

        items.push({
          id: `loan-payment-${loanId}-${payment?.id || payment?._id || index}`,
          date: paymentDate,
          description: payment?.description || `Loan Repayment${loan?.purpose ? ` - ${loan.purpose}` : ""}`,
          type: "Loan Repayment",
          category: "Loan Repayment",
          amount: -paymentAmount,
          shares: null,
          paymentMethod: payment?.paymentMethod || payment?.method || "-",
          reference: payment?.referenceNumber || payment?.reference || payment?.id || "-",
        });
      });
    });

    const ascending = items.sort((a, b) => a.date.getTime() - b.date.getTime());
    let balance = 0;
    return ascending.map((item) => {
      balance += item.amount;
      return { ...item, balance };
    });
  }, [contributions, loans, shareValue]);

  const shareSummary = useMemo(() => {
    return contributions.reduce(
      (summary: { contributionShares: number; savingsShares: number }, contribution: any) => {
        const shares = getContributionShares(contribution, shareValue);
        if (isSavingsContribution(contribution)) {
          summary.savingsShares += shares;
        } else {
          summary.contributionShares += shares;
        }
        return summary;
      },
      { contributionShares: 0, savingsShares: 0 }
    );
  }, [contributions, shareValue]);

  const totalShares = shareSummary.contributionShares + shareSummary.savingsShares;

  const categoryOptions = useMemo(() => {
    const categories = Array.from(new Set(rows.map((row) => row.category).filter(Boolean)));
    return [{ value: ALL, label: "All Categories" }, ...categories.map((category) => ({ value: category, label: category }))];
  }, [rows]);

  const methodOptions = useMemo(() => {
    const methods = Array.from(new Set(rows.map((row) => row.paymentMethod).filter((method) => method && method !== "-")));
    return [{ value: ALL, label: "All Methods" }, ...methods.map((method) => ({ value: method, label: method }))];
  }, [rows]);

  const filteredRows = useMemo(() => {
    const from = dateInputToDate(dateFrom);
    const to = dateInputToDate(dateTo, true);
    return rows.filter((row) => {
      const inDateRange = row.date >= from && row.date <= to;
      const matchesType = typeFilter === ALL || row.type === typeFilter;
      const matchesCategory = categoryFilter === ALL || row.category === categoryFilter;
      const matchesMethod = methodFilter === ALL || row.paymentMethod === methodFilter;
      return inDateRange && matchesType && matchesCategory && matchesMethod;
    });
  }, [rows, dateFrom, dateTo, typeFilter, categoryFilter, methodFilter]);

  const displayRows = useMemo(
    () => [...filteredRows].sort((a, b) => b.date.getTime() - a.date.getTime()),
    [filteredRows]
  );

  const totalPages = Math.max(1, Math.ceil(displayRows.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paginatedRows = displayRows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const showingFrom = displayRows.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(currentPage * PAGE_SIZE, displayRows.length);

  const summary = useMemo(() => {
    const totalIn = filteredRows.reduce((sum, row) => sum + (row.amount > 0 ? row.amount : 0), 0);
    const totalOut = filteredRows.reduce((sum, row) => sum + (row.amount < 0 ? Math.abs(row.amount) : 0), 0);
    const latestBalance = rows.length > 0 ? rows[rows.length - 1].balance : 0;
    return {
      totalIn,
      totalOut,
      netBalance: latestBalance,
      totalTransactions: filteredRows.length,
    };
  }, [filteredRows, rows]);

  const resetPage = () => setPage(1);

  const handleReset = () => {
    setDateFrom(format(startOfYear(new Date()), "yyyy-MM-dd"));
    setDateTo(format(new Date(), "yyyy-MM-dd"));
    setTypeFilter(ALL);
    setCategoryFilter(ALL);
    setMethodFilter(ALL);
    setPage(1);
  };

  const exportRows = (targetRows = displayRows) =>
    targetRows.map((row) => ({
      Date: format(row.date, "dd MMM yyyy, hh:mm a"),
      Description: row.description,
      Type: row.type,
      Category: row.category,
      "Amount (FRW)": Math.round(row.amount),
      Shares: row.shares === null ? "N/A" : Number(row.shares.toFixed(2)),
      "Payment Method": row.paymentMethod,
      Reference: row.reference,
      "Balance (FRW)": Math.round(row.balance),
    }));

  const handleExcelExport = () => {
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(exportRows());
    XLSX.utils.book_append_sheet(workbook, worksheet, "Statement");
    XLSX.writeFile(workbook, `member-statement-${format(new Date(), "yyyy-MM-dd")}.xlsx`);
  };

  const handlePdfDownload = (targetRows = displayRows, fileSuffix = "statement") => {
    const pdf = new jsPDF({ orientation: "landscape" });
    pdf.setFontSize(16);
    pdf.text("My Statement", 14, 16);
    pdf.setFontSize(9);
    pdf.text(
      `${memberName} | ${format(dateInputToDate(dateFrom), "dd MMM yyyy")} - ${format(dateInputToDate(dateTo), "dd MMM yyyy")}`,
      14,
      23
    );
    autoTable(pdf, {
      startY: 30,
      head: [["Date", "Description", "Type", "Category", "Amount", "Shares", "Method", "Reference", "Balance"]],
      body: exportRows(targetRows).map((row) => [
        row.Date,
        row.Description,
        row.Type,
        row.Category,
        row["Amount (FRW)"].toLocaleString(),
        row.Shares,
        row["Payment Method"],
        row.Reference,
        row["Balance (FRW)"].toLocaleString(),
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [0, 154, 68] },
    });
    pdf.save(`member-${fileSuffix}-${format(new Date(), "yyyy-MM-dd")}.pdf`);
  };

  const isLoading = loadingProfile || loadingContributions || loadingLoans;

  if (isLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-10 w-56 rounded-lg bg-slate-200" />
        <div className="h-40 rounded-xl bg-white border border-slate-200" />
        <div className="h-36 rounded-xl bg-white border border-slate-200" />
        <div className="h-80 rounded-xl bg-white border border-slate-200" />
      </div>
    );
  }

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: #fff !important; }
        }
      `}</style>
      <div className="space-y-4 text-[#07184f]">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-normal text-[#07184f]">My Statement</h1>
            <p className="text-sm text-[#4f5f8b]">
              View all your transactions, contributions, savings and loan activities
            </p>
          </div>
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#07184f]">Member Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-5 xl:grid-cols-[1fr_260px]">
              <div className="grid gap-5 md:grid-cols-[220px_repeat(4,1fr)] md:items-center">
                <div className="flex items-center gap-4">
                  <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-[#009A44] text-lg font-bold text-white">
                    {memberInitials}
                  </div>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-[#07184f]">{memberName}</p>
                    <p className="text-xs text-[#4f5f8b]">Member</p>
                    <p className="text-xs text-[#4f5f8b]">ID: {emptyValue(memberCode)}</p>
                  </div>
                </div>
                <InfoField label="Phone" value={emptyValue(profile?.phone)} />
                <InfoField label="Email" value={emptyValue(profile?.email || user?.email)} />
                <InfoField
                  label="Member Since"
                  value={joinedDate ? format(joinedDate, "dd MMM yyyy") : "Not recorded"}
                />
                <InfoField
                  label="Total Shares"
                  value={`${Number(totalShares.toFixed(2)).toLocaleString()} Shares`}
                  helper={shareValue > 0 ? formatMoney(totalShares * shareValue) : undefined}
                  emphasis
                />
              </div>

              <div className="rounded-lg border border-blue-100 bg-blue-50/30 p-4">
                <ShareSource
                  icon={<Coins className="h-4 w-4" />}
                  iconClass="bg-blue-600 text-white"
                  label="From Contributions"
                  shares={shareSummary.contributionShares}
                  amount={shareSummary.contributionShares * shareValue}
                />
                <div className="my-3 h-px bg-blue-100" />
                <ShareSource
                  icon={<WalletCards className="h-4 w-4" />}
                  iconClass="bg-orange-500 text-white"
                  label="From Savings"
                  shares={shareSummary.savingsShares}
                  amount={shareSummary.savingsShares * shareValue}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-sm no-print">
          <CardContent className="p-4">
            <div className="grid gap-4 xl:grid-cols-[1fr_1fr_1fr_1fr_280px]">
              <div>
                <p className="mb-2 text-xs font-semibold text-[#4f5f8b]">Period</p>
                <div className="grid grid-cols-2 gap-2">
                  <DateInput label="From" value={dateFrom} onChange={(value) => { setDateFrom(value); resetPage(); }} />
                  <DateInput label="To" value={dateTo} onChange={(value) => { setDateTo(value); resetPage(); }} />
                </div>
              </div>
              <FilterSelect
                label="Transaction Type"
                value={typeFilter}
                options={TYPE_OPTIONS}
                onChange={(value) => { setTypeFilter(value); resetPage(); }}
              />
              <FilterSelect
                label="Transaction Category"
                value={categoryFilter}
                options={categoryOptions}
                onChange={(value) => { setCategoryFilter(value); resetPage(); }}
              />
              <FilterSelect
                label="Payment Method"
                value={methodFilter}
                options={methodOptions}
                onChange={(value) => { setMethodFilter(value); resetPage(); }}
              />
              <div className="flex items-end gap-2">
                <Button className="h-10 flex-1 bg-[#009A44] hover:bg-[#00843a]">
                  <Filter className="mr-2 h-4 w-4" />
                  Filter
                </Button>
                <Button variant="outline" className="h-10 flex-1" onClick={handleReset}>
                  <RefreshCcw className="mr-2 h-4 w-4" />
                  Reset
                </Button>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button variant="outline" size="sm" onClick={() => handlePdfDownload()}>
                <FileText className="mr-2 h-4 w-4" />
                Download (PDF)
              </Button>
              <Button variant="outline" size="sm" onClick={handleExcelExport}>
                <FileSpreadsheet className="mr-2 h-4 w-4" />
                Export (Excel)
              </Button>
              <Button variant="outline" size="sm" onClick={() => window.print()}>
                <Printer className="mr-2 h-4 w-4" />
                Print
              </Button>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <SummaryCard
            title="Total In (Credits)"
            value={formatMoney(summary.totalIn)}
            icon={<ArrowDown className="h-5 w-5" />}
            tone="green"
          />
          <SummaryCard
            title="Total Out (Debits)"
            value={formatMoney(summary.totalOut)}
            icon={<ArrowUp className="h-5 w-5" />}
            tone="red"
          />
          <SummaryCard
            title="Net Balance"
            value={formatMoney(summary.netBalance)}
            icon={<Equal className="h-5 w-5" />}
            tone="blue"
          />
          <SummaryCard
            title="Total Transactions"
            value={summary.totalTransactions.toLocaleString()}
            icon={<ReceiptText className="h-5 w-5" />}
            tone="purple"
          />
        </div>

        <Card className="border-slate-200 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#07184f]">Transaction History</CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0">
            <div className="overflow-hidden rounded-lg border border-slate-200">
              <Table>
                <TableHeader className="bg-slate-50">
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead className="text-right">Amount (FRW)</TableHead>
                    <TableHead>Shares</TableHead>
                    <TableHead>Payment Method</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead className="text-right">Balance (FRW)</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRows.length > 0 ? (
                    paginatedRows.map((row) => (
                      <TableRow key={row.id} className="hover:bg-slate-50/70">
                        <TableCell className="min-w-[150px] text-xs text-[#07184f]">
                          {format(row.date, "dd MMM yyyy, hh:mm a")}
                        </TableCell>
                        <TableCell className="min-w-[210px] font-medium text-[#07184f]">
                          {row.description}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={`rounded px-2 py-1 text-[11px] ${TYPE_STYLES[row.type]}`}>
                            {row.type}
                          </Badge>
                        </TableCell>
                        <TableCell className="min-w-[150px] text-[#4f5f8b]">{row.category}</TableCell>
                        <TableCell
                          className={`text-right font-bold ${row.amount >= 0 ? "text-[#009A44]" : "text-red-600"}`}
                        >
                          {formatAmount(row.amount)}
                        </TableCell>
                        <TableCell className={row.shares ? "text-[#009A44] font-semibold" : "text-[#4f5f8b]"}>
                          {row.shares === null ? "N/A" : `+${Number(row.shares.toFixed(2)).toLocaleString()} Shares`}
                        </TableCell>
                        <TableCell className="min-w-[130px] text-[#4f5f8b]">{row.paymentMethod}</TableCell>
                        <TableCell className="min-w-[130px] font-mono text-xs text-[#4f5f8b]">
                          {row.reference}
                        </TableCell>
                        <TableCell className="text-right font-semibold text-[#4f5f8b]">
                          {Math.round(row.balance).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-8 w-8">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => handlePdfDownload([row], row.reference || row.id)}>
                                <Download className="h-4 w-4" />
                                Download row
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => navigator.clipboard?.writeText(row.reference)}>
                                <ReceiptText className="h-4 w-4" />
                                Copy reference
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={10} className="h-28 text-center text-sm text-[#4f5f8b]">
                        No transactions found for the selected filters.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-[#4f5f8b]">
                Showing {showingFrom} to {showingTo} of {displayRows.length} entries
              </p>
              <div className="flex items-center gap-2 no-print">
                <PaginationButton disabled={currentPage === 1} onClick={() => setPage(1)}>
                  <ChevronsLeft className="h-4 w-4" />
                </PaginationButton>
                <PaginationButton disabled={currentPage === 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>
                  <ChevronLeft className="h-4 w-4" />
                </PaginationButton>
                {Array.from({ length: totalPages }).slice(0, 5).map((_, index) => {
                  const pageNumber = index + 1;
                  return (
                    <Button
                      key={pageNumber}
                      variant={currentPage === pageNumber ? "default" : "outline"}
                      size="sm"
                      className={currentPage === pageNumber ? "h-8 w-8 bg-[#009A44] hover:bg-[#00843a]" : "h-8 w-8"}
                      onClick={() => setPage(pageNumber)}
                    >
                      {pageNumber}
                    </Button>
                  );
                })}
                <PaginationButton disabled={currentPage === totalPages} onClick={() => setPage((value) => Math.min(totalPages, value + 1))}>
                  <ChevronRight className="h-4 w-4" />
                </PaginationButton>
                <PaginationButton disabled={currentPage === totalPages} onClick={() => setPage(totalPages)}>
                  <ChevronsRight className="h-4 w-4" />
                </PaginationButton>
              </div>
            </div>

            <div className="mt-4 flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
              <Info className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                This statement includes your financial activities in the cooperative. For any questions, please contact the management.
              </span>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function InfoField({
  label,
  value,
  helper,
  emphasis,
}: {
  label: string;
  value: string | number;
  helper?: string;
  emphasis?: boolean;
}) {
  return (
    <div className="border-slate-200 md:border-l md:pl-5">
      <p className="text-xs font-semibold text-[#4f5f8b]">{label}</p>
      <p className={`mt-1 text-sm font-bold ${emphasis ? "text-[#009A44]" : "text-[#07184f]"}`}>{value}</p>
      {helper && <p className="mt-1 text-xs text-[#4f5f8b]">{helper}</p>}
    </div>
  );
}

function ShareSource({
  icon,
  iconClass,
  label,
  shares,
  amount,
}: {
  icon: React.ReactNode;
  iconClass: string;
  label: string;
  shares: number;
  amount: number;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${iconClass}`}>
        {icon}
      </div>
      <div>
        <p className="text-xs text-[#4f5f8b]">{label}</p>
        <p className="text-sm font-bold text-[#07184f]">
          {Number(shares.toFixed(2)).toLocaleString()} Shares
        </p>
        <p className="text-xs text-[#4f5f8b]">({formatMoney(amount)})</p>
      </div>
    </div>
  );
}

function DateInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return (
    <label className="relative block">
      <span className="absolute -top-2 left-3 bg-white px-1 text-[11px] text-[#4f5f8b]">{label}</span>
      <Input
        type="date"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-10 text-sm"
      />
      <Calendar className="pointer-events-none absolute right-3 top-3 h-4 w-4 text-[#4f5f8b]" />
    </label>
  );
}

function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
}) {
  return (
    <div>
      <p className="mb-2 text-xs font-semibold text-[#4f5f8b]">{label}</p>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-10 border border-slate-200 text-sm">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function SummaryCard({
  title,
  value,
  icon,
  tone,
}: {
  title: string;
  value: string;
  icon: React.ReactNode;
  tone: "green" | "red" | "blue" | "purple";
}) {
  const toneClasses = {
    green: {
      card: "border-emerald-100 bg-emerald-50/30",
      text: "text-[#009A44]",
      icon: "bg-emerald-100 text-[#009A44]",
    },
    red: {
      card: "border-red-100 bg-red-50/30",
      text: "text-red-600",
      icon: "bg-red-100 text-red-600",
    },
    blue: {
      card: "border-blue-100 bg-blue-50/30",
      text: "text-blue-600",
      icon: "bg-blue-100 text-blue-600",
    },
    purple: {
      card: "border-violet-100 bg-violet-50/30",
      text: "text-violet-600",
      icon: "bg-violet-100 text-violet-600",
    },
  }[tone];

  return (
    <Card className={`shadow-sm ${toneClasses.card}`}>
      <CardContent className="flex items-center justify-between p-5">
        <div>
          <p className="text-xs font-semibold text-[#4f5f8b]">{title}</p>
          <p className={`mt-3 text-xl font-bold ${toneClasses.text}`}>{value}</p>
        </div>
        <div className={`flex h-10 w-10 items-center justify-center rounded-full ${toneClasses.icon}`}>
          {icon}
        </div>
      </CardContent>
    </Card>
  );
}

function PaginationButton({
  children,
  disabled,
  onClick,
}: {
  children: React.ReactNode;
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <Button variant="outline" size="sm" className="h-8 w-8" disabled={disabled} onClick={onClick}>
      {children}
    </Button>
  );
}
